import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice.ts";
import { useContext, useMemo, useState } from "react";
import { TIME_RANGE_1_MONTHS } from "@shared/common/constants.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import { getFormattedGraphData } from "features/trades/TradesCharts/utils.ts";
import { TRADE_GRAPH_TYPE_TO_KEY } from "features/trades/common/constants.ts";
import { FiltersRequest, TradeGraphType } from "@shared/types";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context.tsx";

interface UseTradesGraphDataProps {
  filters?: FiltersRequest;
  precision?: number;
}

const useTradesGraphData = ({
  filters,
  precision,
}: UseTradesGraphDataProps) => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const [timeRange, setTimeRange] = useState(TIME_RANGE_1_MONTHS);
  const { getTradesQuery } = useContext(TradesPageContext)!;
  const totalChange =
    getTradesQuery?.data?.aggregated_data?.change[displayCurrency];

  const args = useMemo(
    () => ({
      time_range: timeRange,
      filters,
    }),
    [timeRange, filters]
  );

  const {
    data,
    isFetching: graphFetching,
    isSuccess: graphSuccess,
    refetch,
  } = sharedLib.sharedApi.trades.useGetTradesGraphsDataQuery(args);

  const isFetching = getTradesQuery?.isFetching || graphFetching;
  const isSuccess = getTradesQuery?.isSuccess && graphSuccess;

  const deps = [data, timeRange, displayCurrency, precision, totalChange];
  const accumulatedProfitGraphData = useMemo(
    () =>
      getFormattedGraphData(
        data,
        TRADE_GRAPH_TYPE_TO_KEY[TradeGraphType.ACCUMULATING_PROFIT],
        timeRange,
        displayCurrency,
        precision,
        totalChange
      ),
    deps
  );
  const profitByDayGraphData = useMemo(
    () =>
      getFormattedGraphData(
        data,
        TRADE_GRAPH_TYPE_TO_KEY[TradeGraphType.PROFIT_BY_DAY],
        timeRange,
        displayCurrency,
        precision,
        totalChange
      ),
    deps
  );

  const graphTypeToData = {
    [TradeGraphType.ACCUMULATING_PROFIT]: accumulatedProfitGraphData,
    [TradeGraphType.PROFIT_BY_DAY]: profitByDayGraphData,
  };

  return {
    displayCurrency,
    timeRange,
    setTimeRange,
    data,
    isFetching,
    isSuccess,
    accumulatedProfitGraphData,
    profitByDayGraphData,
    graphTypeToData,
    refetch,
  };
};

export default useTradesGraphData;
